'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { useEffect, useRef, useState } from 'react';
import './elina-security.css';

type Turnstile = {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  execute: (id: string) => void;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global { interface Window { turnstile?: Turnstile } }
let scriptPromise: Promise<Turnstile> | undefined;
function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement('script');
    const fail = () => { clearTimeout(timer); script.remove(); reject(new Error('La vérification ne peut pas se charger. Réessayez : votre demande est conservée.')); };
    const timer = setTimeout(fail, 12000);
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true; script.defer = true;
    script.onload = () => { if (window.turnstile) { clearTimeout(timer); resolve(window.turnstile); } else fail(); };
    script.onerror = fail;
    document.head.append(script);
  }).catch(error => { scriptPromise = undefined; throw error; });
  return scriptPromise;
}

type PendingAttempt = { id: string; at: number };
const attempts = new Map<string, PendingAttempt>();
const RETRY_KEY = 'rdv.elina.pending-requests.v1';
async function requestIdentity(url: string, body: BodyInit | null | undefined, locale: string) {
  let bytes: ArrayBuffer | Uint8Array;
  if (typeof body === 'string') bytes = new TextEncoder().encode(body);
  else if (body instanceof FormData && body.get('file') instanceof File) {
    const file = body.get('file') as File;
    const prefix = new TextEncoder().encode(file.name + '\0' + file.type + '\0');
    const content = new Uint8Array(await file.arrayBuffer());
    const joined = new Uint8Array(prefix.length + content.length); joined.set(prefix); joined.set(content, prefix.length); bytes = joined;
  } else throw new Error('Cette demande ne peut pas être envoyée. Actualisez la page pour réessayer.');
  const hash = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  const key = url + ':' + locale + ':' + [...new Uint8Array(hash)].map(x => x.toString(16).padStart(2, '0')).join('');
  try {
    const saved = JSON.parse(sessionStorage.getItem(RETRY_KEY) || '{}')[key];
    if (saved?.id && typeof saved.at === 'number' && saved.at > Date.now() - 15 * 60000) attempts.set(key, saved);
  } catch { /* Retrying also works in this tab without browser storage. */ }
  if (!attempts.has(key) || attempts.get(key)!.at < Date.now() - 15 * 60000) attempts.set(key, { id: crypto.randomUUID(), at: Date.now() });
  for (const [k, value] of attempts) if (value.at < Date.now() - 15 * 60000) attempts.delete(k);
  const persist = () => { try { sessionStorage.setItem(RETRY_KEY, JSON.stringify(Object.fromEntries([...attempts].slice(-10)))); } catch {} };
  persist();
  return { id: attempts.get(key)!.id, confirmed: () => { attempts.delete(key); persist(); } };
}
export type ProtectedFetch = (url: string, init: RequestInit) => Promise<Response>;

export default function usePublicProtection() {
  const { t, locale } = useI18n();
  const panel = useRef<HTMLDivElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const operation = useRef<AbortController | null>(null);
  const retry = useRef<(() => void) | null>(null);
  const active = useRef(true);
  const inFlight = useRef(false);
  const [checking, setChecking] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [retryReady, setRetryReady] = useState(false);
  const [retryMessage, setRetryMessage] = useState('');
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; operation.current?.abort(); };
  }, []);
  useEffect(() => {
    if (checking) panel.current?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }, [checking, interactive, retryMessage]);
  const proof = async (signal: AbortSignal) => {
    signal.throwIfAborted();
    const response = await fetch('/api/public/elina/security', { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) });
    const config = await response.json().catch(() => null);
    if (!response.ok || typeof config?.sitekey !== 'string') throw new Error(config?.error || t('La vérification est indisponible. Votre demande est conservée.'));
    if (!config.sitekey) return '';
    const api = await loadTurnstile();
    signal.throwIfAborted();
    if (!active.current || !host.current) throw new Error(t('Vérification interrompue.'));
    return new Promise<string>((resolve, reject) => {
      let settled = false;
      let generation = 0;
      let widget: string | null = null;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      const removeWidget = () => {
        generation++;
        const previous = widget; widget = null;
        if (previous !== null) { try { api.remove(previous); } catch { /* The SDK may already have removed an expired widget. */ } }
      };
      const abort = () => complete('', new Error(t('Vérification interrompue. Votre demande est conservée.')));
      const complete = (token = '', error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer); clearTimeout(retryTimer); signal.removeEventListener('abort', abort);
        retry.current = null; removeWidget();
        if (active.current) { setInteractive(false); setRetryReady(false); setRetryMessage(''); }
        if (error) reject(error); else resolve(token);
      };
      // Human interaction is separate from the server's response timeout. Never
      // discard a composed message after just one minute waiting for a checkbox.
      const timer = setTimeout(() => complete('', new Error(t('La vérification est restée inactive. Votre demande est conservée ; vous pouvez la renvoyer.'))), 5 * 60000);
      signal.addEventListener('abort', abort, { once: true });
      const renderChallenge = () => {
        if (settled || signal.aborted || !active.current || !host.current) return;
        clearTimeout(retryTimer); removeWidget();
        const attempt = generation;
        const current = () => !settled && attempt === generation && active.current && !signal.aborted;
        setInteractive(false); setRetryReady(false); setRetryMessage('');
        const offerRetry = (message: string) => {
          if (!current()) return;
          clearTimeout(retryTimer); removeWidget();
          setInteractive(false); setRetryReady(true); setRetryMessage(message);
        };
        // A stalled SDK can also be restarted, without posting the request or
        // treating an expired challenge as a completed human verification.
        retryTimer = setTimeout(() => { if (current()) setRetryReady(true); }, 30000);
        try {
          const id = api.render(host.current, {
            sitekey: config.sitekey, language: locale, action: 'elina_public', theme: 'light',
            size: host.current.clientWidth > 0 && host.current.clientWidth < 300 ? 'compact' : 'flexible',
            execution: 'execute', appearance: 'always', retry: 'never',
            'refresh-expired': 'manual', 'refresh-timeout': 'manual',
            callback: (token: string) => { if (current() && token) complete(token); },
            'error-callback': () => { offerRetry(t('La vérification n’a pas abouti. Relancez-la ci-dessous.')); return true; },
            'expired-callback': () => offerRetry(t('La vérification a expiré. Relancez-la ci-dessous.')),
            'timeout-callback': () => offerRetry(t('La vérification a expiré. Relancez-la ci-dessous.')),
            'unsupported-callback': () => offerRetry(t('Ce navigateur ne permet pas la vérification. Essayez sans bloqueur de contenu ou dans un autre navigateur.')),
            'before-interactive-callback': () => { if (current()) setInteractive(true); },
            'after-interactive-callback': () => { if (current()) setInteractive(false); },
          });
          if (!current()) { api.remove(id); return; }
          widget = id;
          api.execute(id);
        } catch { offerRetry(t('La vérification ne peut pas démarrer. Relancez-la ci-dessous.')); }
      };
      retry.current = renderChallenge;
      renderChallenge();
    });
  };
  const fetchProtected: ProtectedFetch = async (url, init) => {
    if (inFlight.current) throw new Error(t('Un traitement est déjà en cours. Patientez avant de réessayer.'));
    inFlight.current = true;
    const controller = new AbortController(); operation.current = controller;
    const requestSignal = init.signal ? AbortSignal.any([init.signal, controller.signal]) : controller.signal;
    setChecking(true); setInteractive(false); setRetryReady(false); setRetryMessage('');
    try {
    const identity = await requestIdentity(url, init.body, locale);
    const token = await proof(requestSignal);
    requestSignal.throwIfAborted();
    if (active.current) setChecking(false);
    const headers = new Headers(init.headers);
    headers.set('x-elina-request-id', identity.id);
    headers.set('x-elina-locale', locale);
    if (token) headers.set('x-elina-challenge', token);
    // The processing timeout starts after any human verification has finished.
    const signal = AbortSignal.any([requestSignal, AbortSignal.timeout(55000)]);
    const response = await fetch(url, { ...init, headers, credentials: 'same-origin', signal });
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { /* Unknown outcome: retain the same retry identity. */ }
    if ((data && data.code !== 'IN_PROGRESS') || response.status === 429) identity.confirmed();
    if (typeof data?.error === 'string') data.error = t(data.error);
    return new Response(data ? JSON.stringify(data) : text, { status: response.status, headers: response.headers });
    } catch (error) {
      if (error instanceof Error && error.name === 'TimeoutError') throw new Error(t('La réponse prend trop de temps. Votre demande est conservée ; vous pouvez réessayer.'));
      if (error instanceof Error && error.name === 'AbortError') throw new Error(t('La demande a été interrompue. Votre texte est conservé.'));
      if (error instanceof TypeError) throw new Error(t('La connexion a été interrompue. Votre demande est conservée ; vous pouvez réessayer.'));
      throw error instanceof Error ? new Error(t(error.message)) : error;
    } finally {
      inFlight.current = false; operation.current = null; retry.current = null;
      if (active.current) { setChecking(false); setInteractive(false); setRetryReady(false); setRetryMessage(''); }
    }
  };
  const challenge = <div ref={panel} className={`elina-security-check${checking ? ' is-checking' : ''}${interactive ? ' is-interactive' : ''}`}>
    {checking && <><p role="status">{retryMessage || (interactive ? t('Confirmez la vérification ci-dessous pour continuer.') : t('Vérification avant l’envoi…'))}</p><p className="elina-security-hint">{t('Votre demande est conservée. Elle sera envoyée dès la vérification terminée.')}</p></>}
    <div ref={host} />
    {checking && <div className="elina-security-actions">{retryReady && <button type="button" className="elina-secondary-button" onClick={() => retry.current?.()}>{t('Relancer la vérification')}</button>}<button type="button" className="elina-text-link" onClick={() => operation.current?.abort()}>{t('Annuler l’envoi')}</button></div>}
  </div>;
  return { fetchProtected, challenge, checking };
}


